'use client'

/**
 * NavMoreSection - the collapsed "More pages" foot of every menu (register: full-nav-trim-1030).
 *
 * The 41 links in MORE_KEYS drew under 20 signed-in people each in 60 days, so they stop costing every
 * visitor a scroll past them. They are NOT removed: a native <details> keeps every link in the DOM
 * (find-in-page still reaches them), and one click shows them.
 *
 * ⭐ A USER WHO NEEDS ONE LOSES NOTHING. Opening it is remembered per browser, and it opens itself
 * when the current page is one of its links, so the active highlight is never hidden.
 *
 * ⚠ Starts closed on the server and settles in an effect, so the server HTML and the first client
 * render agree (no hydration mismatch).
 */

import { useEffect, useState, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { MoreHorizontal, ChevronRight } from 'lucide-react'

const OPEN_KEY = 'dt_nav_more_open'

export default function NavMoreSection({
  count,
  hrefs,
  compact = false,
  className = '',
  rowClassName = 'gap-2 px-2.5 py-[7px] text-sm font-medium',
  children,
}: {
  /** How many links are inside, shown in the summary so the click is a promise with a number. */
  count: number
  /** The hrefs inside, to open the section when the current page is one of them. */
  hrefs: string[]
  /** Icon-only rail: no words fit, so the summary is the icon alone with a tooltip. */
  compact?: boolean
  className?: string
  /** Padding and type for the summary row, so it matches the links of the menu it sits in. */
  rowClassName?: string
  children: ReactNode
}) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const here = !!pathname && hrefs.some((h) => h !== '/' && (pathname === h || pathname.startsWith(h + '/')))
    let remembered = false
    try { remembered = localStorage.getItem(OPEN_KEY) === '1' } catch { /* private mode */ }
    if (here || remembered) setOpen(true)
    // hrefs is rebuilt every render by the caller; the path is what should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <details
      open={open}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open
        if (next === open) return
        setOpen(next)
        try { localStorage.setItem(OPEN_KEY, next ? '1' : '0') } catch { /* choice just will not persist */ }
      }}
      className={`group/more ${className}`}
    >
      <summary
        title={compact ? `More pages (${count})` : undefined}
        className={`flex cursor-pointer list-none items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground [&::-webkit-details-marker]:hidden ${
          compact ? 'justify-center py-3' : rowClassName
        }`}
      >
        <MoreHorizontal className="h-[17px] w-[17px] flex-shrink-0" />
        {!compact && (
          <>
            <span className="truncate">More pages</span>
            <span className="text-[11px] tabular-nums text-muted-foreground/70">{count}</span>
            <ChevronRight className="ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform group-open/more:rotate-90" />
          </>
        )}
      </summary>
      <div className="pt-0.5">{children}</div>
    </details>
  )
}
